import { ColumnDef } from '@tanstack/react-table'
import { ViewCustomer } from '@xeetr/types/models'
import {
  ArrowDown01Icon,
  ArrowDownAZ,
  ArrowUp10Icon,
  ArrowUpDown,
  ArrowUpZA,
  Building,
  Edit,
  Mail,
  Phone,
  Trash2,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

interface CustomerTableColumnsProps {
  onEdit: (customer: ViewCustomer) => void
  onDelete: (customer: ViewCustomer) => void
  bookingCounts: Record<string, number>
}

export const getCustomerTableColumns = ({
  onEdit,
  onDelete,
  bookingCounts,
}: CustomerTableColumnsProps): ColumnDef<ViewCustomer>[] => {
  const baseColumns: ColumnDef<ViewCustomer>[] = [
    {
      accessorKey: 'lastName',
      enableSorting: true,
      meta: {
        displayName: 'Name',
        sortingStrategy: 'server',
      },
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => {
            const sortDir = column.getIsSorted()
            if (sortDir === 'asc' || !sortDir) {
              column.toggleSorting(sortDir === 'asc')
            } else if (sortDir === 'desc') {
              column.clearSorting()
            }
          }}
          className="-ml-4"
        >
          Name
          {column.getIsSorted() === 'asc' && <ArrowDownAZ className="text-primary ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowUpZA className="text-primary ml-2 h-4 w-4" />}
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
        </Button>
      ),
      cell: ({ row }) => {
        const customer = row.original
        const fullName = `${customer.firstName} ${customer.lastName}`
        return <div className="font-medium">{fullName}</div>
      },
    },
    {
      accessorKey: 'companyName',
      enableSorting: true,
      meta: {
        displayName: 'Firma',
        sortingStrategy: 'server', // Server-side sorting
      },
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => {
            const sortDir = column.getIsSorted()
            if (sortDir === 'asc' || !sortDir) {
              column.toggleSorting(sortDir === 'asc')
            } else if (sortDir === 'desc') {
              column.clearSorting()
            }
          }}
          className="-ml-4"
        >
          Firma
          {column.getIsSorted() === 'asc' && <ArrowDownAZ className="text-primary ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowUpZA className="text-primary ml-2 h-4 w-4" />}
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
        </Button>
      ),
      cell: ({ row }) => {
        const companyName = row.getValue('companyName') as string
        return companyName ? (
          <div className="flex items-center gap-1">
            <Building className="text-muted-foreground h-4 w-4" />
            <span>{companyName}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      },
    },
    {
      accessorKey: 'mail',
      enableSorting: true,
      meta: {
        displayName: 'E-Mail',
        sortingStrategy: 'server', // Server-side sorting
      },
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => {
            const sortDir = column.getIsSorted()
            if (sortDir === 'asc' || !sortDir) {
              column.toggleSorting(sortDir === 'asc')
            } else if (sortDir === 'desc') {
              column.clearSorting()
            }
          }}
          className="-ml-4"
        >
          E-Mail
          {column.getIsSorted() === 'asc' && <ArrowDownAZ className="text-primary ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowUpZA className="text-primary ml-2 h-4 w-4" />}
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
        </Button>
      ),
      cell: ({ row }) => {
        const email = row.getValue('mail') as string
        return email ? (
          <div className="flex items-center gap-1">
            <Mail className="text-muted-foreground h-4 w-4" />
            <a
              href={`mailto:${email}`}
              className="hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {email}
            </a>
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      },
    },
    {
      accessorKey: 'phone',
      enableSorting: true,
      meta: {
        displayName: 'Telefon',
        sortingStrategy: 'server', // Server-side sorting
      },
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => {
            const sortDir = column.getIsSorted()
            if (sortDir === 'asc' || !sortDir) {
              column.toggleSorting(sortDir === 'asc')
            } else if (sortDir === 'desc') {
              column.clearSorting()
            }
          }}
          className="-ml-4"
        >
          Telefon
          {column.getIsSorted() === 'asc' && <ArrowDownAZ className="text-primary ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowUpZA className="text-primary ml-2 h-4 w-4" />}
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
        </Button>
      ),
      cell: ({ row }) => {
        const phone = row.getValue('phone') as string
        return phone ? (
          <div className="flex items-center gap-1">
            <Phone className="text-muted-foreground h-4 w-4" />
            <a
              href={`tel:${phone}`}
              className="hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {phone}
            </a>
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      },
    },
    {
      id: 'address',
      enableSorting: true,
      meta: {
        displayName: 'Adresse',
        sortingStrategy: 'server', // Server-side sorting
      },
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => {
            const sortDir = column.getIsSorted()
            if (sortDir === 'asc' || !sortDir) {
              column.toggleSorting(sortDir === 'asc')
            } else if (sortDir === 'desc') {
              column.clearSorting()
            }
          }}
          className="-ml-4"
        >
          Adresse
          {column.getIsSorted() === 'asc' && <ArrowDownAZ className="text-primary ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowUpZA className="text-primary ml-2 h-4 w-4" />}
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
        </Button>
      ),
      cell: ({ row }) => {
        const customer = row.original
        const address = customer.info?.address
        if (!address || (!address.street && !address.postalCode && !address.city)) {
          return <span className="text-muted-foreground">-</span>
        }
        const addressParts = [
          address.street,
          address.postalCode && address.city
            ? `${address.postalCode} ${address.city}`
            : address.city,
        ].filter(Boolean)
        return <span>{addressParts.join(', ')}</span>
      },
    },
    {
      id: 'note',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => {
            const sortDir = column.getIsSorted()
            if (sortDir === 'asc' || !sortDir) {
              column.toggleSorting(sortDir === 'asc')
            } else if (sortDir === 'desc') {
              column.clearSorting()
            }
          }}
          className="-ml-4"
        >
          Notiz
          {column.getIsSorted() === 'asc' && <ArrowDownAZ className="text-primary ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowUpZA className="text-primary ml-2 h-4 w-4" />}
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
        </Button>
      ),
      cell: ({ row }) => {
        const customer = row.original
        const note = customer.info?.note
        return note ? (
          <span className="text-muted-foreground text-sm">{note}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      },
      enableSorting: true,
      meta: {
        displayName: 'Notiz',
      },
    },
    {
      id: 'bookings',
      accessorFn: (row) => (row.id ? bookingCounts[row.id] || 0 : 0),
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => {
            const sortDir = column.getIsSorted()
            if (sortDir === 'asc' || !sortDir) {
              column.toggleSorting(sortDir === 'asc')
            } else if (sortDir === 'desc') {
              column.clearSorting()
            }
          }}
          className="-ml-4"
        >
          Buchungen
          {column.getIsSorted() === 'asc' && (
            <ArrowDown01Icon className="text-primary ml-2 h-4 w-4" />
          )}
          {column.getIsSorted() === 'desc' && (
            <ArrowUp10Icon className="text-primary ml-2 h-4 w-4" />
          )}
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
        </Button>
      ),
      cell: ({ row }) => {
        const customer = row.original
        const count = customer.id ? bookingCounts[customer.id] || 0 : 0
        return count > 0 ? (
          <Badge variant="secondary" className="text-xs">
            {count} {count === 1 ? 'Buchung' : 'Buchungen'}
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      },
      enableSorting: true,
      meta: {
        displayName: 'Buchungen',
        sortingStrategy: 'client', // Sort client-side on current page data
      },
    },
  ]

  const actionsColumn = generateCustomerActionsColumn(onEdit, onDelete, bookingCounts)

  return [...baseColumns, actionsColumn]
}

export const generateCustomerActionsColumn = (
  onEdit: (customer: ViewCustomer) => void,
  onDelete: (customer: ViewCustomer) => void,
  bookingCounts: Record<string, number>
): ColumnDef<ViewCustomer> => ({
  id: 'edit',
  header: 'Aktionen',
  cell: ({ row }) => {
    const customer = row.original
    const bookingCount = customer.id ? bookingCounts[customer.id] || 0 : 0
    const canDelete = bookingCount === 0

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            onEdit(customer)
          }}
        >
          <Edit className="mr-1 h-4 w-4" />
          Bearbeiten
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-red-600 hover:bg-red-50 hover:text-red-700"
          disabled={!canDelete}
          onClick={(e) => {
            e.stopPropagation()
            onDelete(customer)
          }}
        >
          <Trash2 className="mr-1 h-4 w-4" />
          Löschen
        </Button>
      </div>
    )
  },
  enableSorting: false,
  enableHiding: false,
  meta: {
    displayName: 'Aktionen',
  },
})
